import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ScheduleComponent,
  ViewsDirective,
  ViewDirective,
  Inject,
  Day,
  Week,
  WorkWeek,
  Month,
  Resize,
  DragAndDrop,
} from "@syncfusion/ej2-react-schedule";
import { DialogUtility } from '@syncfusion/ej2-popups';
import fetchApi from '../../Lib/api';
import { Modal, Group, Box, Checkbox, Button, Loader, Text, Stack } from '@mantine/core';
import { createElement } from '@syncfusion/ej2-base';
import useUserStore from '../../../stores/userStore';
import { useTranslation } from "react-i18next";
// This is the modal component, refactored from the old CalenderImportForm
function CalendarImportModal({ isOpen, onClose, onComplete }) {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (isOpen) {
      const fetchCalendars = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetchApi('/google_calendars/list_calendars');
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch calendars');
          }
          const data = await response.json();
          setCalendars(data);
          const initial = {};
          data.forEach(cal => {
            initial[cal.id] = { checked: false };
          });
          setSelected(initial);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchCalendars();
    }
  }, [isOpen]);

  const handleCheck = (calendarId, checked) => {
    setSelected(prev => ({ ...prev, [calendarId]: { ...prev[calendarId], checked } }));
  };

  const handleSubmit = async () => {
    const selectedCalendarIds = Object.entries(selected)
      .filter(([_, v]) => v.checked)
      .map(([calendarId]) => calendarId);

    if (selectedCalendarIds.length === 0) {
      setError('Please select at least one calendar.');
      return;
    }

    try {
      // The method should be POST for merge, as it changes state on the server
      await fetchApi(`/google_calendars/merge?google_calendar_ids=${selectedCalendarIds.join(',')}`, 'POST');
      onComplete(); // This will close the modal and trigger a sync/refresh
    } catch (error) {
      setError('Failed to save calendars. Please try again.');
    }
  };

  return (
    <Modal opened={isOpen} onClose={onClose} title="Select Google Calendars to Sync" centered size="lg">
      {loading ? (
        <Group position="center" py="xl"><Loader /></Group>
      ) : error ? (
        <Text color="red" align="center">{error}</Text>
      ) : (
        <Box>
          <Stack spacing="md">
            {calendars.length === 0 ? (
              <Text align="center" color="dimmed">No calendars found or access denied.</Text>
            ) : (
              calendars.filter(c => c.access_role === "owner").map(cal => (
                <Group key={cal.id} position="apart" align="center" spacing="xs" style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                  <Group align="center" spacing="xs">
                    <Checkbox
                      checked={selected[cal.id]?.checked || false}
                      onChange={e => handleCheck(cal.id, e.currentTarget.checked)}
                      label={<span><b>{cal.summary}</b> {cal.primary && <span style={{ color: '#4285F4', fontWeight: 500 }}>(Primary)</span>}</span>}
                    />
                    <Text size="xs" color="gray">Access: {cal.access_role}</Text>
                  </Group>
                </Group>
              ))
            )}
          </Stack>
          <Group position="right" mt="xl">
            <Button onClick={handleSubmit} color="blue" size="md">Save and Sync</Button>
          </Group>
        </Box>
      )}
    </Modal>
  );
}


export default function Calendar() {
  const scheduleObj = useRef(null);
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState("Month");
  const [selectedDate] = useState(new Date());


  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    return { start: startOfWeek, end: endOfWeek };
  });

  const [isLoading, setIsLoading] = useState(false);
  // New state variables for Google integration
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { user: currentUser, isLoading: isUserLoading } = useUserStore();
   const { t } = useTranslation();
  

  const triggerSync = async () => {
    if (!isGoogleConnected) return;
    setIsSyncing(true);
    try {
      await fetchApi('/google_calendars/sync', 'POST');
      // After sync is triggered, we wait a bit and then refresh events from our local DB
      setTimeout(() => {
        fetchEvents(dateRange.start, dateRange.end, false); // false to prevent re-triggering sync
        setIsSyncing(false);
      }, 3000); // 3-second delay to give the background job time to run
    } catch (error) {
      console.error('Error starting sync:', error);
      setIsSyncing(false);
    }
  };

  // This useEffect handles the entire initial loading sequence, driven by the user from the store.
  useEffect(() => {
    // Don't do anything until we have the user from the store.
    if (isUserLoading || !currentUser) {
      return;
    }

    const initializeCalendar = async () => {
      setIsLoading(true);
      try {
        // Step 1: Fetch local events immediately now that we have a user.
        await fetchEvents(dateRange.start, dateRange.end, false);

        // Step 2: Check Google connection status
        const connResponse = await fetchApi('/google_calendars/check_connection');
        if (!connResponse.ok) throw new Error('Could not check connection');
        const connData = await connResponse.json();
        setIsGoogleConnected(connData.connected);

        // Step 3: Decide the next action based on connection status
        const params = new URLSearchParams(window.location.hash.split('?')[1]);
        if (params.get('new_connection') === 'true') {
          // User just finished the OAuth flow, show the modal to select calendars
          setIsModalOpen(true);
          window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
        } else if (connData.connected) {
          if (connData.has_selected_calendars) {
            // User is connected and has calendars, trigger a background sync
            triggerSync();
          } else {
            // User is connected but hasn't chosen calendars, prompt them
            setIsModalOpen(true);
          }
        }
      } catch (error) {
        console.error('Error during calendar initialization:', error);
        // Optionally set an error state to show in the UI
      } finally {
        setIsLoading(false);
      }
    };

    initializeCalendar();
  }, [currentUser, isUserLoading]); // This effect now runs when the user state changes.

  // This useEffect is now only for handling date range changes from navigation
  useEffect(() => {
    if (currentUser) { // Ensure user is loaded before fetching
      fetchEvents(dateRange.start, dateRange.end);
    }
  }, [dateRange, currentUser]); // Re-fetch if the date range or user changes

  // This useEffect sets up a periodic sync every 5 minutes
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (isGoogleConnected) {
        console.log("Performing periodic background sync...");
        triggerSync();
      }
    }, 300000); // 300000 ms = 5 minutes

    // Clean up the interval when the component unmounts
    return () => clearInterval(syncInterval);
  }, [isGoogleConnected]); // Rerun if the connection status changes

  const fetchEvents = async (startDate, endDate, shouldSync = true) => {
    setIsLoading(true);
    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      // Always fetch from the local DB
      const response = await fetchApi(`/events?start_date=${start}&end_date=${end}`);

      if (!response.ok) {
        // ... error handling ...
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();

      console.log(data)

      const transformedEvents = data.map(event => ({
        Id: event.id,
        Subject: event.subject,
        Description: event.description,
        StartTime: new Date(event.start_time),
        EndTime: new Date(event.end_time),
        Location: event.location,
        RecurrenceRule: event.recurrence_rule,
        parentId: event.parent_id,
        source: event.source,
        CategoryColor: event.color,
        // Use the definitive is_owner flag from the backend
        is_owner: event.is_owner,
        organizer: event.organizer,
        attendees: event.attendees || [],
        isReadonly: !event.is_owner, // Directly use the backend's ownership status
      }));

      setEvents(transformedEvents);

      // Trigger background sync if connected
      if (isGoogleConnected && shouldSync) {
        triggerSync();
      }

    } catch (err) {
      // ... error handling ...
    } finally {
      setIsLoading(false);
    }
  };

  // ... (onNavigating and handleRecurringEvent can stay the same)
  const onNavigating = (args) => {
    const currentDate = args.currentDate || new Date();
    const currentViewOption = args.currentView || currentView;

    let startDate, endDate;
    const weekPaddingDays = 7;
    const monthPaddingMonths = 1;

    switch(currentViewOption) {
      case 'Day':
        startDate = new Date(currentDate);
        endDate = new Date(currentDate);
        startDate.setDate(startDate.getDate() - weekPaddingDays);
        endDate.setDate(endDate.getDate() + weekPaddingDays);
        break;
      case 'Week':
      case 'WorkWeek':
        startDate = new Date(currentDate);
        startDate.setDate(startDate.getDate() - startDate.getDay());
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        startDate.setDate(startDate.getDate() - weekPaddingDays);
        endDate.setDate(endDate.getDate() + weekPaddingDays);
        break;
      case 'Month':
      default:
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        startDate.setMonth(startDate.getMonth() - monthPaddingMonths);
        endDate.setMonth(endDate.getMonth() + monthPaddingMonths + 1, 0);
    }

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return;
    }

    const newRange = { start: startDate, end: endDate };
    if (dateRange.start.toISOString() !== newRange.start.toISOString() ||
        dateRange.end.toISOString() !== newRange.end.toISOString()) {
      setDateRange(newRange);
    }

    if(args.currentView && args.currentView !== currentView) {
      setCurrentView(args.currentView);
    }

    args.cancel = false;
  };

  const handleRecurringEvent = (eventData, action) => {
    const isSeriesMaster = eventData.RecurrenceRule && !eventData.RecurrenceID;
    const isOccurrence = eventData.RecurrenceID || eventData.parentId;
  
    if (!isSeriesMaster && !isOccurrence) {
      return Promise.resolve(null);
    }
  
    let dialogRef = null;
  
    return new Promise((resolve) => {
      dialogRef = DialogUtility.confirm({
        title: 'Recurring Event Action',
        content: `You are attempting to ${action} a recurring event. Do you want to ${action} only this occurrence, all future occurrences, or the entire series?`,
        okButton: {
          text: 'Entire Series',
          click: () => {
            resolve('all');
            dialogRef.close();
          }
        },
        cancelButton: {
          text: 'Cancel',
          click: () => {
            resolve(null);
          }
        },
        showCloseIcon: true,
        closeOnEscape: true,
        position: { X: 'center', Y: 'center' },
        buttons: [
          {
            click: () => {
              resolve('single');
              dialogRef.close();
            },
            buttonModel: { content: 'Only This Occurrence' }
          },
          {
            click: () => {
              resolve('future');
              dialogRef.close();
            },
            buttonModel: { content: 'All Future Occurrences' }
          }
        ]
      });
    });
  };

  // New function to handle RSVP responses
  const handleRsvp = async (eventId, status) => {
    console.log(`RSVP button clicked. Event ID: ${eventId}, Status: ${status}`);

    if (!eventId) {
      console.error("handleRsvp failed: eventId is missing or invalid.");
      alert("Could not submit RSVP: The event ID is missing.");
      return;
    }

    setIsLoading(true);
    try {
      console.log("Sending RSVP request to the backend...");
      await fetchApi(`/events/${eventId}/rsvp`, 'POST', { status });
      console.log("RSVP request successful.");

      // Close the editor and refresh events to show the updated status
      if (scheduleObj.current) {
        scheduleObj.current.closeEditor();
      }
      fetchEvents(dateRange.start, dateRange.end, false);
    } catch (error) {
      console.error('Failed to update RSVP status:', error);
      alert(`An error occurred while submitting your RSVP: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Simplified createEvent - no longer needs to know about Google
  const createEvent = async (eventData) => {
    const attendeeEmails = eventData.Attendees ? eventData.Attendees.split(',').map(e => e.trim()).filter(e => e) : [];

    const payload = {
      title: eventData.Subject,
      description: eventData.Description || '',
      start_time: eventData.StartTime.toISOString(),
      end_time: eventData.EndTime.toISOString(),
      location: eventData.Location || '',
      recurrence_rule: eventData.RecurrenceRule || null,
      color: eventData.CategoryColor,
      attendees: attendeeEmails,
      video_link: eventData.VideoLink || null,
    };

    const response = await fetchApi('/events', 'POST', { event: payload });

    if (!response.ok) {
      // ... error handling ...
      throw new Error('Failed to create event');
    }
    return await response.json();
  };

  // updateEvent and deleteEvent are mostly the same, but we remove the 'source' param
  const updateEvent = async (eventData, recurrenceAction = null) => {
    const isOccurrence = eventData.isRecurring || eventData.RecurrenceID || eventData.parentId;
    const isSeriesMaster = eventData.RecurrenceRule && !eventData.RecurrenceID;

    const eventIdForBackend = isOccurrence ? (eventData.RecurrenceID || eventData.parentId) : eventData.Id;

    if (!eventIdForBackend) {
      console.error("Could not determine the master event ID for the update.", eventData);
      throw new Error("Cannot determine event ID for update.");
    }

    // This is the critical fix:
    // If the 'Attendees' string from the form exists, use it as the source of truth.
    // Otherwise, fall back to the original 'attendees' array to preserve them.
    let attendeeEmails = [];
    if (typeof eventData.Attendees === 'string') {
      attendeeEmails = eventData.Attendees.split(',').map(e => e.trim()).filter(e => e);
    } else if (Array.isArray(eventData.attendees)) {
      attendeeEmails = eventData.attendees.map(a => a.email);
    }

    let payload;

    if (recurrenceAction === 'all') {
      // When updating an entire series, we must preserve the original start time of the series.
      const masterEvent = events.find(e => e.Id === eventIdForBackend);

      if (!masterEvent) {
        console.error("Could not find the master event in the current state. The event might be outside the currently loaded date range.");
        alert("Could not save changes. Please ensure the start of the recurring series is visible in your current calendar view and try again.");
        throw new Error("Could not find the master event to update.");
      }

      // Calculate the duration from the newly edited data.
      const newDuration = eventData.EndTime.getTime() - eventData.StartTime.getTime();
      // Apply the new duration to the original start time to get the correct new end time.
      const newEndTime = new Date(masterEvent.StartTime.getTime() + newDuration);

      // The payload uses the original start time but all other new data from the editor.
      payload = {
        title: eventData.Subject,
        description: eventData.Description || '',
        start_time: masterEvent.StartTime.toISOString(), // Preserve original start time
        end_time: newEndTime.toISOString(), // Use new duration
        location: eventData.Location || '',
        recurrence_rule: eventData.RecurrenceRule, // Use new rule from editor
        color: eventData.CategoryColor,
        attendees: attendeeEmails,
        video_link: eventData.VideoLink || null,
      };

    } else {
      // For single or non-recurring events, we use the event data as-is.
      payload = {
        title: eventData.Subject,
        description: eventData.Description || '',
        start_time: eventData.StartTime.toISOString(),
        end_time: eventData.EndTime.toISOString(),
        location: eventData.Location || '',
        recurrence_rule: (isSeriesMaster) ? eventData.RecurrenceRule : null,
        color: eventData.CategoryColor,
        attendees: attendeeEmails,
        video_link: eventData.VideoLink || null,
      };
    }

    let url = `/events/${eventIdForBackend}`;
    const params = new URLSearchParams();
    if (recurrenceAction) {
      params.append('recurrence_action', recurrenceAction);
      if ((recurrenceAction === 'single' || recurrenceAction === 'future') && eventData.StartTime) {
        params.append('occurrence_date', eventData.StartTime.toISOString());
      }
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetchApi(url, 'PUT', { event: payload });

    if (!response.ok) {
      const errorBody = await response.json();
      console.error('Failed to update event:', errorBody);
      throw new Error('Failed to update event');
    }
    
    return await response.json();
  };

  const deleteEvent = async (eventData, recurrenceAction = null) => {
    let eventIdForBackend;

    // For a recurring action, we always need the ID of the master event.
    if (recurrenceAction) {
      // If it's an occurrence, RecurrenceID holds the master ID.
      // If we're on the master event itself, its own Id is the master ID.
      eventIdForBackend = eventData.RecurrenceID || eventData.Id;
    } else {
      // For a simple non-recurring event, just use its own ID.
      eventIdForBackend = eventData.Id;
    }

    // A fallback just in case, using the lowercase 'id' property.
    if (!eventIdForBackend) {
      eventIdForBackend = eventData.id;
    }

    if (!eventIdForBackend) {
      console.error("DELETE FAILED: Could not determine a valid event ID.", eventData);
      throw new Error("Cannot determine event ID for deletion");
    }

    console.log(`Attempting to delete event with backend ID: ${eventIdForBackend}`);

    let url = `/events/${eventIdForBackend}`;
    const params = new URLSearchParams();
    if (recurrenceAction) {
      params.append('recurrence_action', recurrenceAction);
      if ((recurrenceAction === 'single' || recurrenceAction === 'future') && eventData.StartTime) {
        params.append('occurrence_date', eventData.StartTime.toISOString());
      }
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetchApi(url, 'DELETE');

    if (!response.ok) {
      console.error(`DELETE FAILED: API request failed with status ${response.status}`, response);
      throw new Error('Failed to delete event');
    }
    console.log(`DELETE SUCCEEDED for event ID: ${eventIdForBackend}`);
  };

  // onActionBegin can be simplified
  const onActionBegin = async (args) => {
    const crudActions = ['eventCreate', 'eventChange', 'eventRemove'];
    if (!crudActions.includes(args.requestType)) {
      return;
    }
  
    // Prevent the default Syncfusion action, as we will handle everything manually.
    args.cancel = true;
    setIsLoading(true);
  
    try {
      let shouldRefresh = false;
      // A more robust way to get the event data from the arguments.
      // It prioritizes changedRecords for updates, and falls back to data for create/delete.
      const eventData = (args.changedRecords && args.changedRecords[0]) || (args.data && args.data[0]);

      // If eventData is still undefined, we cannot proceed. This prevents crashes.
      if (!eventData) {
        console.error(`Could not determine event data for action: ${args.requestType}`, args);
        setIsLoading(false);
        return;
      }
  
      // Permission check for edit/delete
      if (args.requestType === 'eventChange' || args.requestType === 'eventRemove') {
        if (!eventData.is_owner) {
          DialogUtility.alert({
            title: 'Permission Denied',
            content: 'You do not have permission to modify or delete this event.',
            okButton: { text: 'OK' }
          });
          setIsLoading(false);
          return;
        }
      }
  
      switch (args.requestType) {
        case 'eventCreate':
          await createEvent(eventData);
          shouldRefresh = true;
          break;
        
        case 'eventChange': {
          // Merge the original event data with the changes from the form.
          // This ensures we don't lose data like the original attendee list.
          const originalEventData = args.data[0] || args.data;
          const changedEventData = args.changedRecords[0] || args.changedRecords;
          const completeEventData = { ...originalEventData, ...changedEventData };

          const isRecurringEvent = completeEventData.RecurrenceRule || completeEventData.isRecurring || completeEventData.RecurrenceID || completeEventData.parentId;
          if (isRecurringEvent) {
            const recurrenceAction = await handleRecurringEvent(completeEventData, 'update');
            if (recurrenceAction) {
              await updateEvent(completeEventData, recurrenceAction);
              shouldRefresh = true;
            }
          } else {
            await updateEvent(completeEventData, null);
            shouldRefresh = true;
          }
          break;
        }
  
        case 'eventRemove': {
          const isRecurringEvent = eventData.RecurrenceRule || eventData.isRecurring || eventData.RecurrenceID || eventData.parentId;
          if (isRecurringEvent) {
            const recurrenceAction = await handleRecurringEvent(eventData, 'delete');
            if (recurrenceAction) {
              await deleteEvent(eventData, recurrenceAction);
              shouldRefresh = true;
            }
          } else {
            await deleteEvent(eventData, null);
            shouldRefresh = true;
          }
          break;
        }
      }
  
      if (shouldRefresh) {
        fetchEvents(dateRange.start, dateRange.end);
      }
  
    } catch (error) {
      console.error(`Failed to ${args.requestType}:`, error);
      // Optionally show an error dialog to the user
    } finally {
      setIsLoading(false);
      // Close the editor window as the action is complete
      if (scheduleObj.current) {
        scheduleObj.current.closeEditor();
      }
    }
  };

  const handleGoogleAuth = () => {
    if (isGoogleConnected) {
      setIsModalOpen(true);
    } else {
      window.location.href = '/google_calendars/oauth_redirect';
    }
  };

  // Template for how events are rendered in the calendar view
  const eventTemplate = (props) => {
    const currentUserAttendee = props.attendees?.find(a => a.email === currentUser?.email);
    let rsvpStatusIndicator = null;

    if (props.is_owner) {
      rsvpStatusIndicator = <span style={{ fontSize: '10px', color: '#6c757d' }}> (Owner)</span>;
    } else if (currentUserAttendee) {
      switch (currentUserAttendee.status) {
        case 'accepted':
          rsvpStatusIndicator = <span style={{ fontSize: '10px', color: 'green' }}> (Going)</span>;
          break;
        case 'declined':
          rsvpStatusIndicator = <span style={{ fontSize: '10px', color: 'red' }}> (Not Going)</span>;
          break;
        case 'tentative':
          rsvpStatusIndicator = <span style={{ fontSize: '10px', color: 'orange' }}> (Maybe)</span>;
          break;
        default:
          rsvpStatusIndicator = <span style={{ fontSize: '10px', color: 'gray' }}> (Pending)</span>;
      }
    }

    return (
      <div className="event-template">
        <div>{props.Subject}</div>
        {rsvpStatusIndicator}
      </div>
    );
  };

  const onPopupOpen = (args) => {
    if (args.type !== 'Editor') {
      return;
    }
  
    const eventData = args.data;
    const isNewEvent = !eventData.Id;
    const formElement = args.element.querySelector('.e-schedule-form');

    // --- 1. ALWAYS RESET THE FORM TO A CLEAN, EDITABLE STATE ---
    if (formElement) {
      // Remove any custom fields from a previous opening
      formElement.querySelectorAll('.custom-event-field').forEach(field => field.remove());

      // Re-enable all standard inputs
      formElement.querySelectorAll('input, textarea').forEach(input => {
        input.removeAttribute('disabled');
      });
      // Reset styles on complex components that were manually disabled
      const componentsToReset = [
        '.e-repeat-element',
        '.e-recurrence-editor',
        '.e-all-day-time-zone-row'
      ];
      componentsToReset.forEach(selector => {
        const el = formElement.querySelector(selector);
        if (el) {
          const wrapper = el.closest('.e-input-wrapper') || el;
          wrapper.style.pointerEvents = '';
          wrapper.style.opacity = '';
        }
      });
      // Ensure the footer buttons are visible by default
      const footer = args.element.querySelector('.e-footer-content');
      if (footer) {
        footer.style.display = 'block';
      }
    }
  
    // --- 2. ADD OR MANAGE CUSTOM FIELDS ---
    if (isNewEvent) {
      // Logic for adding custom fields to a new event...
      if (formElement) {
        const existingCustomRow = formElement.querySelector('.custom-attendees-row');
        if (existingCustomRow) return;
      }
      const customRow = createElement('div', { className: 'e-title-location-row custom-attendees-row' });
      const attendeesContainer = createElement('div', { className: 'e-subject-container' });
      const attendeesWrapper = createElement('div', { className: 'e-float-input e-control-wrapper e-input-group' });
      const attendeesInput = createElement('input', {
          className: 'e-field', name: 'Attendees', id: 'Attendees', type: 'text',
          attrs: { 'aria-labelledby': 'label_Attendees', 'placeholder': 'Enter emails, comma-separated', 'data-name': 'Attendees' }
      });
      const attendeesLine = createElement('span', { className: 'e-float-line' });
      const attendeesLabel = createElement('label', { className: 'e-float-text e-label-top', id: 'label_Attendees', innerHTML: 'Attendees', attrs: { for: 'Attendees' } });
      attendeesWrapper.append(attendeesInput, attendeesLine, attendeesLabel);
      attendeesContainer.appendChild(attendeesWrapper);
      const videoContainer = createElement('div', { className: 'e-location-container' });
      const videoWrapper = createElement('div', { className: 'e-float-input e-control-wrapper e-input-group' });
      const videoInput = createElement('input', {
          className: 'e-field', name: 'VideoLink', id: 'VideoLink', type: 'text',
          attrs: { 'aria-labelledby': 'label_VideoLink', 'placeholder': 'Add video link', 'data-name': 'VideoLink' }
      });
      const videoLine = createElement('span', { className: 'e-float-line' });
      const videoLabel = createElement('label', { className: 'e-float-text e-label-top', id: 'label_VideoLink', innerHTML: 'Video Link', attrs: { for: 'VideoLink' } });
      videoWrapper.append(videoInput, videoLine, videoLabel);
      videoContainer.appendChild(videoWrapper);
      customRow.append(attendeesContainer, videoContainer);
      if (formElement) {
          const descriptionRow = formElement.querySelector('.e-description-row');
          if (descriptionRow?.parentNode) {
              descriptionRow.parentNode.insertBefore(customRow, descriptionRow);
          } else {
              formElement.appendChild(customRow);
          }
      }
    } else {
      // Logic for managing fields on an existing event...
      const isOwner = eventData.is_owner;

      const currentUserAttendee = currentUser?.email && Array.isArray(eventData.attendees) && eventData.attendees.find(a => a.email?.toLowerCase() === currentUser.email.toLowerCase());
      
      if (eventData.organizer) {
        const organizerRow = createElement('div', { className: 'e-form-group custom-event-field' });
        organizerRow.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-bottom: 8px; display: block;">Organizer</label>';
        const organizerEl = createElement('div', { className: 'e-text-ellipsis', innerHTML: eventData.organizer.email });
        organizerRow.appendChild(organizerEl);
        if (formElement) formElement.appendChild(organizerRow);
      }
      
      if (isOwner) {
        const attendeeEmails = eventData.attendees?.map(a => a.email).join(', ') || '';
        const attendeesRow = createElement('div', { className: 'e-form-group custom-event-field' });
        attendeesRow.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-block: 8px; display: block;">Attendees</label>';
        const attendeesInput = createElement('input', {
          className: 'e-field e-input', 
          name: 'Attendees', 
          value: attendeeEmails,
          attrs: { 
            'placeholder': 'Enter emails, comma-separated', 
            'data-name': 'Attendees' 
          }
        });
        eventData.Attendees = attendeeEmails;
        attendeesRow.appendChild(attendeesInput);
        if (formElement) formElement.appendChild(attendeesRow);
      } else if (eventData.attendees?.length > 0) {
        const attendeeContainer = createElement('div', { className: 'e-form-group custom-event-field' });
        attendeeContainer.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-block: 8px; display: block;">Attendees</label>';
        const attendeeList = createElement('div', { className: 'attendee-list' });
        eventData.attendees?.forEach(attendee => {
          const listItem = createElement('div', { 
            className: 'attendee-list-item', 
            innerHTML: `<span>${attendee.email}</span><span class="attendee-status"> (${attendee.status})</span>` 
          });
          attendeeList.appendChild(listItem);
        });
        attendeeContainer.appendChild(attendeeList);
        if (formElement) formElement.appendChild(attendeeContainer);
      }
      if (isOwner) {
        const videoRow = createElement('div', { className: 'e-form-group custom-event-field' });
        videoRow.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-bottom: 8px; display: block;">Video Link</label>';
        const videoInput = createElement('input', {
            className: 'e-field e-input', name: 'VideoLink', value: eventData.video_link || '',
            attrs: { 'placeholder': 'Add video link', 'data-name': 'VideoLink' }
        });
        eventData.VideoLink = eventData.video_link || '';
        videoRow.appendChild(videoInput);
        if (formElement) formElement.appendChild(videoRow);
      } else if (eventData.video_link) {
        const videoContainer = createElement('div', { className: 'e-form-group custom-event-field' });
        videoContainer.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-bottom: 8px; display: block;">Video Link</label>';
        const videoEl = createElement('a', { href: eventData.video_link, target: '_blank', rel: 'noopener noreferrer', innerHTML: eventData.video_link, className: 'video-link' });
        videoContainer.appendChild(videoEl);
        if (formElement) formElement.appendChild(videoContainer);
      }
      if (!isOwner && currentUserAttendee) {
        if (currentUserAttendee.status === 'needsAction') {
          const rsvpContainer = createElement('div', { className: 'e-form-group custom-event-field' });
          rsvpContainer.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-bottom: 8px; display: block;">Your RSVP</label>';
          const rsvpButtons = createElement('div', { className: 'e-button-group' });
          const yesButton = createElement('button', { innerHTML: 'Yes', className: 'e-btn e-primary' });
          const noButton = createElement('button', { innerHTML: 'No', className: 'e-btn' });
          const maybeButton = createElement('button', { innerHTML: 'Maybe', className: 'e-btn' });
          yesButton.onclick = () => handleRsvp(eventData.Id, 'accepted');
          noButton.onclick = () => handleRsvp(eventData.Id, 'declined');
          maybeButton.onclick = () => handleRsvp(eventData.Id, 'tentative');
          rsvpButtons.append(yesButton, noButton, maybeButton);
          rsvpContainer.appendChild(rsvpButtons);
          if (formElement) formElement.appendChild(rsvpContainer);
        } else {
          const rsvpStatusContainer = createElement('div', { className: 'e-form-group custom-event-field' });
          rsvpStatusContainer.innerHTML = '<label class="e-label" style="font-weight: 500; font-size: 13px; margin-bottom: 8px; display: block;">Your RSVP</label>';
          const statusText = createElement('div', {
            className: 'e-text-ellipsis',
            innerHTML: `You have responded: <strong>${currentUserAttendee.status.charAt(0).toUpperCase() + currentUserAttendee.status.slice(1)}</strong>`
          });
          rsvpStatusContainer.appendChild(statusText);
          if (formElement) formElement.appendChild(rsvpStatusContainer);
        }
      }
      
    }

    // --- 3. APPLY READ-ONLY STATE IF THE USER IS A GUEST ---
    // This should only ever apply to existing events. New events are always editable.
    if (!isNewEvent) {
      const isOwner = eventData.is_owner;
      if (!isOwner) {
        // Disable all standard input fields
        formElement.querySelectorAll('input, textarea').forEach(input => {
          input.setAttribute('disabled', true);
        });
        // Disable complex components
        const componentsToDisable = [
          '.e-repeat-element',
          '.e-recurrence-editor',
          '.e-all-day-time-zone-row'
        ];
        componentsToDisable.forEach(selector => {
          const el = formElement.querySelector(selector);
          if (el) {
            const wrapper = el.closest('.e-input-wrapper') || el;
            wrapper.style.pointerEvents = 'none';
            wrapper.style.opacity = '0.6';
            wrapper.style.cursor = 'not-allowed';
          }
        });
        // Hide the footer buttons
        const footer = args.element.querySelector('.e-footer-content');
        if (footer) {
          footer.style.display = 'none';
        }
      }
    }
  };



  return (
    <div className="calendar-container" style={{ position: 'relative' }}>
      <Group position="right" style={{ padding: '10px' }}>
        {isSyncing && <Loader size="xs" />}
        <Button onClick={handleGoogleAuth} color="blue" size="xs">
        {isGoogleConnected  ? t("google_calendar.manage"): t("google_calendar.connect")}
 
        </Button>
      </Group>

      {isLoading && <div className="loading-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1000 }}><Loader /></div>}
      
      <ScheduleComponent
        width="100%"
        height="800px"
        ref={scheduleObj}
        selectedDate={selectedDate}
        currentView={currentView}
        eventSettings={{
          dataSource: events,
          template: eventTemplate.bind(this)
        }}
        popupOpen={onPopupOpen}
        actionBegin={onActionBegin}
        navigating={onNavigating}
      >
        <ViewsDirective>
          <ViewDirective option="Day" />
          <ViewDirective option="Week" />
          <ViewDirective option="WorkWeek" />
          <ViewDirective option="Month" />
        </ViewsDirective>
        <Inject services={[Day, Week, WorkWeek, Month, Resize, DragAndDrop]} />
      </ScheduleComponent>

      <CalendarImportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onComplete={() => {
          setIsModalOpen(false);
          // Trigger a sync immediately after saving calendars
          triggerSync();
        }}
      />
    </div>
  );
}